import AppBarSpacer from '../components/common/AppBarSpacer';
import {
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableContainer,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Tooltip,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import Done from '@mui/icons-material/Done';
import Replay from '@mui/icons-material/Replay';
import SearchIcon from '@mui/icons-material/Search';
import Visibility from '@mui/icons-material/Visibility';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Cancel from '@mui/icons-material/Cancel';
import Groups from '@mui/icons-material/Groups';
import HowToReg from '@mui/icons-material/HowToReg';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import ViewList from '@mui/icons-material/ViewList';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import type { Dayjs } from 'dayjs';
import { saveAs } from 'file-saver';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import AssessmentMiniDrawer from '../components/AssessmentMiniDrawer';
import ClassCalendarView from '../components/ClassCalendar/ClassCalendarView';
import ColorChip from '../components/common/ColorChip';
import SofLevelChip from '../components/common/SofLevelChip';
import CoachStudentCardList from '../components/CoachStudentCardList';
import { useIsMobile } from '../hooks/useResponsive';
import {
  MANAGEMENT_TABLE_HEADER_COLOR,
  ManagementTableHeaderCell,
  managementTableRowSx,
} from '../components/common/ManagementTable';
import PageHeader from '../components/common/PageHeader';
import { apiGet, apiPost } from '../utils/apiClient';
import {
  serviceColors,
  serviceTypeColors,
  userTypeColors,
} from '../utils/colorHandler';
import { notifyAPIClient, notifyFailure } from '../utils/toast';
import { useClassCalendar } from '../hooks/useClassCalendar';
import { useWeeklyClassSheet } from '../hooks/useWeeklyClassSheet';
import type {
  ClassCalendarFilters,
  ClassCalendarItem,
} from '../types/ClassCalendar.type';
import { appendClassCalendarFilters } from '../utils/classCalendarQuery';
import type {
  CoachAttendance,
  CoachAttendanceHistoryResponse,
  CoachAttendanceResponse,
  CoachClass,
  CoachClassPackage,
  CoachClassResponse,
  CoachClassStudent,
} from '../types/CoachClass.type';

const timeSlots = [
  { value: 'All', label: 'All Time Slots' },
  { value: 'morning', label: 'Morning (06:00-12:00)' },
  { value: 'afternoon', label: 'Afternoon (12:00-18:00)' },
  { value: 'evening', label: 'Evening (18:00-22:00)' },
];

const getTimeSlot = (time: string | null): string => {
  if (!time) return '';

  const [hours, minutes] = time.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return '';

  const totalMinutes = hours * 60 + minutes;
  if (totalMinutes >= 360 && totalMinutes < 720) return 'morning';
  if (totalMinutes >= 720 && totalMinutes < 1080) return 'afternoon';
  if (totalMinutes >= 1080 && totalMinutes < 1320) return 'evening';
  return '';
};

const formatClassDate = (value: string | null): string => {
  if (!value) return '---';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '---'
    : date.toLocaleDateString('en-GB');
};

const formatDateFilterValue = (value: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const formatClassTime = (
  startTime: string | null,
  endTime: string | null,
): string => {
  if (!startTime && !endTime) return '---';
  return `${startTime || '---'} - ${endTime || '---'}`;
};

const formatStudentDate = (value: string): string => {
  if (!value) return '---';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '---'
    : date.toLocaleDateString('en-US');
};

const formatPackageDate = (value: string): string => {
  if (!value) return '---';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '---';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${date.getFullYear()}`;
};

const isPackageExpired = (value: string): boolean => {
  const expiryDate = new Date(value);
  return !Number.isNaN(expiryDate.getTime()) && expiryDate < new Date();
};

const getPackageRows = (student: CoachClassStudent): CoachClassPackage[] => {
  if (student.packages?.length) return student.packages;

  return [
    {
      id: student.registrationId,
      course: student.details,
      registrationStartDate: student.registrationStartDate,
      registrationEndDate: student.registrationEndDate,
      visitType: student.visitType,
      remainingSessions: student.remainingSessions,
    },
  ];
};

const checkInStatusColors: Record<string, string> = {
  CHECKED_IN: '#11734b',
  CHECKED_IN_BG: '#d4edbc',
  NOT_CHECKED_IN: '#bc1e1d',
  NOT_CHECKED_IN_BG: '#ffcfc9',
};

const statusLabel = (status: CoachClass['status']): string => {
  if (status === 'ACTIVE') return 'Active';
  if (status === 'CLOSED') return 'Closed';
  return 'Cancelled';
};

const statusColor = (status: CoachClass['status']): string => {
  if (status === 'ACTIVE') return '#4caf50';
  if (status === 'CLOSED') return '#f44336';
  return '#ff9800';
};

const selectSx = {
  minWidth: 150,
  '& .MuiSelect-select': {
    py: 1,
  },
};

const levelColumnSx = {
  width: 140,
  minWidth: 140,
  whiteSpace: 'nowrap' as const,
};

const CoachClassPage = () => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(true);
  const [classes, setClasses] = useState<CoachClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [exportWeekLoading, setExportWeekLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [serviceTypeFilter, setServiceTypeFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [levelFilter, setLevelFilter] = useState('All');
  const [timeSlotFilter, setTimeSlotFilter] = useState('All');
  const [teacherFilter, setTeacherFilter] = useState('All');
  const [dateFilter, setDateFilter] = useState('');
  const [calendarView, setCalendarView] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [studentPage, setStudentPage] = useState(0);
  const [studentRowsPerPage, setStudentRowsPerPage] = useState(10);
  const [studentSearch, setStudentSearch] = useState('');
  const [selectedStudentIds, setSelectedStudentIds] = useState<Set<number>>(
    new Set(),
  );
  const [packageStudent, setPackageStudent] =
    useState<CoachClassStudent | null>(null);
  const [checkInStudent, setCheckInStudent] =
    useState<CoachClassStudent | null>(null);
  const [checkInSubmitting, setCheckInSubmitting] = useState(false);
  const [historyStudent, setHistoryStudent] =
    useState<CoachClassStudent | null>(null);
  const [history, setHistory] = useState<CoachAttendance[]>([]);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(10);
  const [historyTotalItems, setHistoryTotalItems] = useState(0);
  const [historyFilterDate, setHistoryFilterDate] = useState<Dayjs | null>(
    null,
  );
  const [historyLoading, setHistoryLoading] = useState(false);
  const [undoAttendance, setUndoAttendance] = useState<CoachAttendance | null>(
    null,
  );
  const [undoSubmitting, setUndoSubmitting] = useState(false);

  const refreshClasses = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const response = await apiGet<CoachClassResponse>(
        '/class/coach/my-classes',
      );
      setClasses(response.data || []);
      setLastSyncTime(new Date());
    } catch {
      notifyFailure();
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshClasses();
  }, [refreshClasses]);

  // Refresh at midnight so the Check-in action is recalculated for the new day.
  useEffect(() => {
    let timeoutId: number;

    const scheduleNextRefresh = () => {
      const now = new Date();
      const nextDay = new Date(now);
      nextDay.setHours(24, 0, 0, 0);

      timeoutId = window.setTimeout(
        () => {
          void refreshClasses(false);
          scheduleNextRefresh();
        },
        Math.max(nextDay.getTime() - now.getTime(), 1000),
      );
    };

    scheduleNextRefresh();
    return () => window.clearTimeout(timeoutId);
  }, [refreshClasses]);

  // Also refresh when returning to the app after it was backgrounded overnight.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void refreshClasses(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refreshClasses]);

  const serviceTypeOptions = useMemo(
    () => [
      'All',
      ...Array.from(
        new Set(classes.map((classItem) => classItem.course).filter(Boolean)),
      ),
    ],
    [classes],
  );

  const levelOptions = useMemo(
    () => [
      'All',
      ...Array.from(
        new Set(
          classes
            .map((classItem) => classItem.level)
            .filter((level): level is string => Boolean(level)),
        ),
      ),
    ],
    [classes],
  );

  const teacherOptions = useMemo(
    () => [
      'All',
      ...Array.from(
        new Set(classes.flatMap((classItem) => classItem.coachTeachers)),
      ),
    ],
    [classes],
  );

  const filteredClasses = useMemo(() => {
    const query = search.trim().toLowerCase();

    return classes.reduce<CoachClass[]>((result, classItem) => {
      const matchesFilters =
        (serviceTypeFilter === 'All' ||
          classItem.course === serviceTypeFilter) &&
        (statusFilter === 'All' || classItem.status === statusFilter) &&
        (levelFilter === 'All' || classItem.level === levelFilter) &&
        (teacherFilter === 'All' ||
          classItem.coachTeachers.includes(teacherFilter)) &&
        (!dateFilter ||
          formatDateFilterValue(classItem.classDate) === dateFilter) &&
        (timeSlotFilter === 'All' ||
          getTimeSlot(classItem.startTime) === timeSlotFilter);

      if (!matchesFilters) return result;

      if (!query) {
        result.push(classItem);
        return result;
      }

      const matchesClass = [
        classItem.id,
        classItem.name,
        classItem.course,
        classItem.level,
        classItem.status,
        ...classItem.coachTeachers,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));

      if (matchesClass) {
        result.push(classItem);
        return result;
      }

      const matchingStudents = classItem.students.filter((student) =>
        [
          student.name,
          student.firstName,
          student.lastName,
          student.parentName,
          student.email,
          student.phone,
          student.course,
          student.level,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query)),
      );

      if (matchingStudents.length > 0) {
        result.push({ ...classItem, students: matchingStudents });
      }

      return result;
    }, []);
  }, [
    classes,
    levelFilter,
    search,
    serviceTypeFilter,
    statusFilter,
    teacherFilter,
    timeSlotFilter,
    dateFilter,
  ]);

  const calendarFilters = useMemo<ClassCalendarFilters>(
    () => ({
      search,
      subtype: serviceTypeFilter,
      status: statusFilter,
      level: levelFilter,
      timeSlot: timeSlotFilter,
      coachName: teacherFilter === 'All' ? undefined : teacherFilter,
      date: dateFilter || undefined,
    }),
    [
      dateFilter,
      levelFilter,
      search,
      serviceTypeFilter,
      statusFilter,
      teacherFilter,
      timeSlotFilter,
    ],
  );

  const classCalendar = useClassCalendar({
    role: 'coach',
    enabled: calendarView,
    filters: calendarFilters,
  });

  const weeklyClassSheet = useWeeklyClassSheet({
    role: 'coach',
    enabled: calendarView,
    range: classCalendar.range,
    filters: calendarFilters,
  });

  const handleCalendarSelect = (item: ClassCalendarItem) => {
    setSelectedClassId(item.id);
  };

  const handleExportWeekReport = async (startDate: string) => {
    if (exportWeekLoading) return;

    setExportWeekLoading(true);
    try {
      const params = appendClassCalendarFilters(
        new URLSearchParams(),
        calendarFilters,
      );
      const workbook = await apiGet<Blob>(
        `/class/export-week-report/${startDate}?${params.toString()}`,
        { responseType: 'blob' },
      );

      const subtype = calendarFilters.subtype || 'all';
      saveAs(
        workbook,
        `weekly_class_sheet_${subtype.toLowerCase()}_${startDate}.xlsx`,
      );
      notifyAPIClient('Export successful!');
    } catch (error) {
      const status = (error as { response?: { status?: number } }).response
        ?.status;

      if (status === 404) {
        notifyAPIClient('No data found for this week');
      } else if (status === 403 || status === 401) {
        notifyAPIClient('You do not have permission to export the report');
      } else {
        notifyAPIClient('Export failed. Please try again!');
      }
    } finally {
      setExportWeekLoading(false);
    }
  };

  useEffect(() => {
    setPage(0);
  }, [
    search,
    serviceTypeFilter,
    statusFilter,
    levelFilter,
    timeSlotFilter,
    teacherFilter,
    dateFilter,
  ]);

  useEffect(() => {
    if (
      selectedClassId !== null &&
      !classes.some((classItem) => classItem.id === selectedClassId)
    ) {
      setSelectedClassId(null);
    }
  }, [classes, selectedClassId]);

  const paginatedClasses = filteredClasses.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );
  const selectedClass = classes.find(
    (classItem) => classItem.id === selectedClassId,
  );
  const filteredStudents = useMemo(() => {
    if (!selectedClass) return [];

    const query = studentSearch.trim().toLowerCase();
    if (!query) return selectedClass.students;

    return selectedClass.students.filter((student) =>
      [student.name, student.firstName, student.lastName, student.preferredName]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  }, [selectedClass, studentSearch]);

  const paginatedStudents = filteredStudents
    ? filteredStudents.slice(
        studentPage * studentRowsPerPage,
        studentPage * studentRowsPerPage + studentRowsPerPage,
      )
    : [];
  const totalClassStudents = selectedClass?.students.length || 0;
  const totalCheckedInToday =
    selectedClass?.students.filter((student) => student.checkedInToday)
      .length || 0;
  const checkableStudents = paginatedStudents.filter(
    (student) => !student.checkedInToday,
  );
  const allCheckableStudentsSelected =
    checkableStudents.length > 0 &&
    checkableStudents.every((student) =>
      selectedStudentIds.has(student.registrationId),
    );
  const someCheckableStudentsSelected = checkableStudents.some((student) =>
    selectedStudentIds.has(student.registrationId),
  );

  useEffect(() => {
    setStudentPage(0);
  }, [selectedClassId, studentSearch]);

  useEffect(() => {
    setSelectedStudentIds(new Set());
  }, [selectedClassId, studentSearch, studentPage, studentRowsPerPage]);

  useEffect(() => {
    if (selectedClassId === null) setStudentSearch('');
  }, [selectedClassId]);

  useEffect(() => {
    if (!selectedClass || !historyStudent) {
      setHistory([]);
      setHistoryTotalItems(0);
      return;
    }

    let mounted = true;
    const loadHistory = async () => {
      try {
        setHistoryLoading(true);
        const date = historyFilterDate?.format('YYYY-MM-DD') || '';
        const response = await apiGet<CoachAttendanceHistoryResponse>(
          `/class/coach/${selectedClass.id}/students/${historyStudent.registrationId}/check-in-history?page=${historyPage + 1}&limit=${historyRowsPerPage}&date=${date}`,
        );
        if (mounted) {
          setHistory(response.data || []);
          setHistoryTotalItems(response.metadata?.totalItems || 0);
        }
      } catch (error) {
        if (mounted) {
          const message = (
            error as { response?: { data?: { message?: string } } }
          ).response?.data?.message;
          notifyAPIClient(message || 'Unable to load check-in history.');
        }
      } finally {
        if (mounted) setHistoryLoading(false);
      }
    };

    void loadHistory();
    return () => {
      mounted = false;
    };
  }, [
    historyFilterDate,
    historyPage,
    historyRowsPerPage,
    historyStudent,
    selectedClass,
  ]);

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleStudentRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setStudentRowsPerPage(Number(event.target.value));
    setStudentPage(0);
  };

  const handleOpenHistory = (
    event: React.MouseEvent,
    student: CoachClassStudent,
  ) => {
    event.stopPropagation();
    setHistoryPage(0);
    setHistoryFilterDate(null);
    setHistoryStudent(student);
  };

  const handleOpenCheckIn = (
    event: React.MouseEvent,
    student: CoachClassStudent,
  ) => {
    event.stopPropagation();
    if (student.checkedInToday) return;
    setCheckInStudent(student);
  };

  const handleOpenPackage = (
    event: React.MouseEvent,
    student: CoachClassStudent,
  ) => {
    event.stopPropagation();
    setPackageStudent(student);
  };

  const handleStudentSelectionChange = (
    registrationId: number,
    checked: boolean,
  ) => {
    setSelectedStudentIds((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(registrationId);
      } else {
        next.delete(registrationId);
      }
      return next;
    });
  };

  const handleSelectAllStudents = (checked: boolean) => {
    setSelectedStudentIds(
      checked
        ? new Set(checkableStudents.map((student) => student.registrationId))
        : new Set(),
    );
  };

  const handleConfirmCheckIn = async () => {
    if (!selectedClass || !checkInStudent) return;

    try {
      setCheckInSubmitting(true);
      const response = await apiPost<
        CoachAttendanceResponse,
        Record<string, never>
      >(
        `/class/coach/${selectedClass.id}/students/${checkInStudent.registrationId}/check-in`,
        {},
      );
      notifyAPIClient(response.message || 'Check in successfully!');
      setClasses((currentClasses) =>
        currentClasses.map((classItem) =>
          classItem.id !== selectedClass.id
            ? classItem
            : {
                ...classItem,
                students: classItem.students.map((student) =>
                  student.registrationId === checkInStudent.registrationId
                    ? {
                        ...student,
                        checkedInToday: true,
                        remainingSessions:
                          student.visitType === 'count' &&
                          student.remainingSessions !== null
                            ? Math.max(student.remainingSessions - 1, 0)
                            : student.remainingSessions,
                      }
                    : student,
                ),
              },
        ),
      );
      setSelectedStudentIds((current) => {
        const next = new Set(current);
        next.delete(checkInStudent.registrationId);
        return next;
      });
      setCheckInStudent(null);
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } })
        .response?.data?.message;
      notifyAPIClient(message || 'Unable to check in this student.');
      await refreshClasses(false);
    } finally {
      setCheckInSubmitting(false);
    }
  };

  const handleBulkCheckIn = async () => {
    if (!selectedClass || selectedStudentIds.size === 0) return;

    const studentsToCheckIn = selectedClass.students.filter(
      (student) =>
        selectedStudentIds.has(student.registrationId) &&
        !student.checkedInToday,
    );

    if (studentsToCheckIn.length === 0) {
      setSelectedStudentIds(new Set());
      return;
    }

    try {
      setCheckInSubmitting(true);
      const results = await Promise.allSettled(
        studentsToCheckIn.map((student) =>
          apiPost<CoachAttendanceResponse, Record<string, never>>(
            `/class/coach/${selectedClass.id}/students/${student.registrationId}/check-in`,
            {},
          ),
        ),
      );
      const successfulRegistrationIds = results.flatMap((result, index) =>
        result.status === 'fulfilled'
          ? [studentsToCheckIn[index].registrationId]
          : [],
      );
      const failedCount = results.length - successfulRegistrationIds.length;

      setClasses((currentClasses) =>
        currentClasses.map((classItem) =>
          classItem.id !== selectedClass.id
            ? classItem
            : {
                ...classItem,
                students: classItem.students.map((student) =>
                  successfulRegistrationIds.includes(student.registrationId)
                    ? {
                        ...student,
                        checkedInToday: true,
                        remainingSessions:
                          student.visitType === 'count' &&
                          student.remainingSessions !== null
                            ? Math.max(student.remainingSessions - 1, 0)
                            : student.remainingSessions,
                      }
                    : student,
                ),
              },
        ),
      );
      setSelectedStudentIds(new Set());

      if (failedCount > 0) {
        await refreshClasses(false);
      }

      notifyAPIClient(
        `${successfulRegistrationIds.length} student(s) checked in successfully${
          failedCount > 0 ? `; ${failedCount} failed` : ''
        }.`,
      );
    } finally {
      setCheckInSubmitting(false);
    }
  };

  const handleConfirmUndo = async () => {
    if (!selectedClass || !historyStudent || !undoAttendance) return;

    try {
      setUndoSubmitting(true);
      const response = await apiPost<
        CoachAttendanceResponse,
        Record<string, never>
      >(
        `/class/coach/${selectedClass.id}/students/${historyStudent.registrationId}/check-in-history/${undoAttendance.id}/undo`,
        {},
      );
      notifyAPIClient(response.message);
      setHistory((current) =>
        current.filter((item) => item.id !== undoAttendance.id),
      );
      setHistoryTotalItems((current) => Math.max(current - 1, 0));
      const undoneDate = new Date(undoAttendance.attendanceDate);
      const today = new Date();
      const wasCheckedInToday =
        undoneDate.getFullYear() === today.getFullYear() &&
        undoneDate.getMonth() === today.getMonth() &&
        undoneDate.getDate() === today.getDate();
      if (wasCheckedInToday) {
        setClasses((currentClasses) =>
          currentClasses.map((classItem) =>
            classItem.id !== selectedClass.id
              ? classItem
              : {
                  ...classItem,
                  students: classItem.students.map((student) =>
                    student.registrationId === historyStudent.registrationId
                      ? {
                          ...student,
                          checkedInToday: false,
                          remainingSessions:
                            student.visitType === 'count' &&
                            student.remainingSessions !== null
                              ? student.remainingSessions + 1
                              : student.remainingSessions,
                        }
                      : student,
                  ),
                },
          ),
        );
      }
      setUndoAttendance(null);
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } })
        .response?.data?.message;
      notifyAPIClient(message || 'Unable to undo this check-in.');
    } finally {
      setUndoSubmitting(false);
    }
  };

  return (
    <div className="flex h-screen">
      <AssessmentMiniDrawer open={open} setOpen={setOpen} />

      <main className="flex-1 overflow-auto app-page-bg">
        <AppBarSpacer />
        <div className="h-full px-6 py-4">
          <PageHeader
            title="Class Management"
            subtitle="View your assigned classes and students."
          />

          <Card elevation={2}>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  flexWrap: 'wrap',
                  p: 1.25,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              >
                <TextField
                  size="small"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search by class name or class ID"
                  inputProps={{ 'aria-label': 'Search classes' }}
                  sx={{ width: 320, maxWidth: '100%' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                  }}
                >
                  <FormControl size="small">
                    <Select
                      value={serviceTypeFilter}
                      onChange={(event) =>
                        setServiceTypeFilter(event.target.value)
                      }
                      aria-label="Filter by service type"
                      sx={selectSx}
                    >
                      {serviceTypeOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option === 'All' ? 'All Service Types' : option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small">
                    <Select
                      value={statusFilter}
                      onChange={(event) => setStatusFilter(event.target.value)}
                      aria-label="Filter by status"
                      sx={selectSx}
                    >
                      <MenuItem value="All">All Status</MenuItem>
                      <MenuItem value="ACTIVE">Active</MenuItem>
                      <MenuItem value="CLOSED">Closed</MenuItem>
                      <MenuItem value="CANCELLED">Cancelled</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl size="small">
                    <Select
                      value={levelFilter}
                      onChange={(event) => setLevelFilter(event.target.value)}
                      aria-label="Filter by level"
                      sx={selectSx}
                    >
                      {levelOptions.map((option) => (
                        <MenuItem key={option || 'empty-level'} value={option}>
                          {option === 'All' ? 'All Levels' : option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small">
                    <Select
                      value={timeSlotFilter}
                      onChange={(event) =>
                        setTimeSlotFilter(event.target.value)
                      }
                      aria-label="Filter by time slot"
                      sx={selectSx}
                    >
                      {timeSlots.map((slot) => (
                        <MenuItem key={slot.value} value={slot.value}>
                          {slot.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small">
                    <Select
                      value={teacherFilter}
                      onChange={(event) => setTeacherFilter(event.target.value)}
                      aria-label="Filter by teacher"
                      sx={selectSx}
                    >
                      {teacherOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option === 'All' ? 'All Teachers' : option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    size="small"
                    type="date"
                    label="Class Date"
                    value={dateFilter}
                    onChange={(event) => setDateFilter(event.target.value)}
                    aria-label="Filter by class date"
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 165 }}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  px: 1.5,
                  py: 1,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.background.default,
                }}
              >
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={calendarView ? 'calendar' : 'list'}
                  onChange={(_, nextView: 'list' | 'calendar' | null) => {
                    if (nextView) setCalendarView(nextView === 'calendar');
                  }}
                  aria-label="coach class management view mode"
                >
                  <ToggleButton value="list" aria-label="list view">
                    <ViewList fontSize="small" sx={{ mr: 0.5 }} />
                    List View
                  </ToggleButton>
                  <ToggleButton value="calendar" aria-label="view calendar">
                    <CalendarMonth fontSize="small" sx={{ mr: 0.5 }} />
                    View Calendar
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {calendarView ? (
                <ClassCalendarView
                  view={classCalendar.view}
                  anchorDate={classCalendar.anchorDate}
                  range={classCalendar.range}
                  items={classCalendar.items}
                  metadata={classCalendar.metadata}
                  loading={classCalendar.loading}
                  error={classCalendar.error}
                  onViewChange={classCalendar.setView}
                  onToday={classCalendar.goToToday}
                  onDateChange={classCalendar.goToDate}
                  onPrevious={classCalendar.goToPrevious}
                  onNext={classCalendar.goToNext}
                  onRetry={classCalendar.refresh}
                  onSelectItem={handleCalendarSelect}
                  onExportWeek={() => {
                    void handleExportWeekReport(classCalendar.range.from);
                  }}
                  exportLoading={exportWeekLoading}
                  sheetDays={weeklyClassSheet.days}
                  sheetMetadata={weeklyClassSheet.metadata}
                  sheetLoading={weeklyClassSheet.loading}
                  sheetError={weeklyClassSheet.error}
                  onSheetRetry={weeklyClassSheet.refresh}
                  sheetSubtype={serviceTypeFilter}
                  onSheetSubtypeChange={setServiceTypeFilter}
                  onBackToList={() => setCalendarView(false)}
                />
              ) : (
                <>
                  <Box sx={{ px: 1.5, pt: 1.25 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      color="text.primary"
                    >
                      Assigned Classes ({filteredClasses.length})
                    </Typography>
                  </Box>

                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ mt: 1 }}
                  >
                    <Table size="small" aria-label="Assigned classes">
                      <TableHead>
                        <TableRow
                          sx={{
                            backgroundColor: MANAGEMENT_TABLE_HEADER_COLOR,
                          }}
                        >
                          <ManagementTableHeaderCell>
                            Class Name
                          </ManagementTableHeaderCell>
                          <ManagementTableHeaderCell>
                            Date
                          </ManagementTableHeaderCell>
                          <ManagementTableHeaderCell>
                            Time
                          </ManagementTableHeaderCell>
                          <ManagementTableHeaderCell>
                            Class Type
                          </ManagementTableHeaderCell>
                          <ManagementTableHeaderCell>
                            Level
                          </ManagementTableHeaderCell>
                          <ManagementTableHeaderCell>
                            Coach Teacher
                          </ManagementTableHeaderCell>
                          <ManagementTableHeaderCell>
                            Status
                          </ManagementTableHeaderCell>
                          <ManagementTableHeaderCell>
                            Students
                          </ManagementTableHeaderCell>
                          <ManagementTableHeaderCell>
                            Attendance
                          </ManagementTableHeaderCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loading ? (
                          <TableRow>
                            <TableCell
                              colSpan={9}
                              align="center"
                              sx={{ py: 5 }}
                            >
                              <CircularProgress size={28} />
                            </TableCell>
                          </TableRow>
                        ) : paginatedClasses.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={9}
                              align="center"
                              sx={{ py: 5 }}
                            >
                              No assigned classes found.
                            </TableCell>
                          </TableRow>
                        ) : (
                          paginatedClasses.map((classItem) => {
                            const isSelected = selectedClassId === classItem.id;
                            return (
                              <TableRow
                                key={classItem.id}
                                hover
                                selected={isSelected}
                                onClick={() => setSelectedClassId(classItem.id)}
                                sx={{
                                  ...managementTableRowSx(isSelected),
                                  cursor: 'pointer',
                                }}
                              >
                                <TableCell
                                  sx={{ minWidth: 260, fontWeight: 600 }}
                                >
                                  {classItem.name}
                                </TableCell>
                                <TableCell>
                                  {formatClassDate(classItem.classDate)}
                                </TableCell>
                                <TableCell>
                                  {formatClassTime(
                                    classItem.startTime,
                                    classItem.endTime,
                                  )}
                                </TableCell>
                                <TableCell>
                                  <ColorChip
                                    label={classItem.course}
                                    palette={serviceTypeColors}
                                  />
                                </TableCell>
                                <TableCell sx={levelColumnSx}>
                                  <SofLevelChip
                                    level={classItem.level}
                                    serviceType={classItem.course}
                                  />
                                </TableCell>
                                <TableCell>
                                  {classItem.coachTeachers.length > 0
                                    ? classItem.coachTeachers.join(', ')
                                    : '---'}
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    component="span"
                                    variant="body2"
                                    fontWeight="bold"
                                    sx={{
                                      color: statusColor(classItem.status),
                                    }}
                                  >
                                    {statusLabel(classItem.status)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {classItem.students.length}
                                </TableCell>
                                <TableCell>
                                  {
                                    classItem.students.filter(
                                      (student) => student.checkedInToday,
                                    ).length
                                  }{' '}
                                  / {classItem.students.length}
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      pl: 1.5,
                      borderTop: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Last sync:{' '}
                      {lastSyncTime
                        ? lastSyncTime.toLocaleString('en-GB')
                        : '---'}
                    </Typography>
                    <TablePagination
                      component="div"
                      count={filteredClasses.length}
                      page={page}
                      onPageChange={(_, nextPage) => setPage(nextPage)}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleRowsPerPageChange}
                      rowsPerPageOptions={[5, 10, 25, 50, 100]}
                      labelRowsPerPage="Rows per page:"
                    />
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <Dialog
        open={Boolean(selectedClass)}
        onClose={() => setSelectedClassId(null)}
        fullScreen={isMobile}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle>
          {selectedClass?.name || 'Class Students'}
          <Typography variant="body2" color="text.secondary">
            {selectedClass
              ? `${formatClassDate(selectedClass.classDate)} · ${selectedClass.startTime || '---'} - ${selectedClass.endTime || '---'}`
              : ''}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1.5,
              }}
            >
              <CheckCircle sx={{ color: '#4da7db', fontSize: 24 }} />
              <Typography
                variant="h6"
                fontWeight="700"
                sx={{ color: '#4da7db' }}
              >
                Check-in Summary
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, minmax(0, 1fr))',
                },
                gap: 2,
              }}
            >
              <Card
                variant="outlined"
                sx={{ borderRadius: 2, textAlign: 'center' }}
              >
                <CardContent
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    py: 2.5,
                    '&:last-child': { pb: 2.5 },
                  }}
                >
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: alpha(theme.palette.primary.main, 0.14),
                      color: '#4da7db',
                    }}
                  >
                    <Groups sx={{ fontSize: 38 }} />
                  </Box>
                  <Typography
                    variant="h6"
                    fontWeight="700"
                    color="text.primary"
                  >
                    Total Students
                  </Typography>
                  <Typography variant="h4" fontWeight="700" color="#4da7db">
                    {totalClassStudents}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Students enrolled in this class
                  </Typography>
                </CardContent>
              </Card>

              <Card
                variant="outlined"
                sx={{ borderRadius: 2, textAlign: 'center' }}
              >
                <CardContent
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    py: 2.5,
                    '&:last-child': { pb: 2.5 },
                  }}
                >
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: alpha(theme.palette.success.main, 0.16),
                      color: '#2e7d32',
                    }}
                  >
                    <HowToReg sx={{ fontSize: 38 }} />
                  </Box>
                  <Typography
                    variant="h6"
                    fontWeight="700"
                    color="text.primary"
                  >
                    Checked In Today
                  </Typography>
                  <Typography variant="h4" fontWeight="700" color="#2e7d32">
                    {totalCheckedInToday} / {totalClassStudents}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Attendance recorded today
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              mb: 1.5,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={<HowToReg />}
              disabled={selectedStudentIds.size === 0 || checkInSubmitting}
              onClick={() => void handleBulkCheckIn()}
            >
              Check in selected
              {selectedStudentIds.size > 0
                ? ` (${selectedStudentIds.size})`
                : ''}
            </Button>
            <TextField
              size="small"
              value={studentSearch}
              onChange={(event) => setStudentSearch(event.target.value)}
              placeholder="Search by student name"
              inputProps={{ 'aria-label': 'Search students by name' }}
              sx={{ width: 320, maxWidth: '100%' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {selectedClass && (
            <Typography
              variant="h6"
              fontWeight={700}
              color="#53AADC"
              sx={{ mb: 1.5 }}
            >
              Coach Teacher: {selectedClass.coachTeachers.join(', ') || '---'}
            </Typography>
          )}

          {isMobile ? (
            <CoachStudentCardList
              students={paginatedStudents}
              selectedStudentIds={selectedStudentIds}
              checkInSubmitting={checkInSubmitting}
              allSelected={allCheckableStudentsSelected}
              someSelected={someCheckableStudentsSelected}
              selectAllDisabled={
                checkableStudents.length === 0 || checkInSubmitting
              }
              isExpired={(student) =>
                isPackageExpired(student.registrationEndDate)
              }
              onSelectAll={handleSelectAllStudents}
              onToggleSelect={handleStudentSelectionChange}
              onOpenPackage={handleOpenPackage}
              onOpenHistory={handleOpenHistory}
              onOpenCheckIn={handleOpenCheckIn}
            />
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table
                size="small"
                aria-label="Class students"
                sx={{ minWidth: 1350 }}
              >
                <TableHead>
                  <TableRow
                    sx={{ backgroundColor: MANAGEMENT_TABLE_HEADER_COLOR }}
                  >
                    <TableCell
                      padding="checkbox"
                      sx={{ backgroundColor: MANAGEMENT_TABLE_HEADER_COLOR }}
                    >
                      <Checkbox
                        size="small"
                        checked={allCheckableStudentsSelected}
                        indeterminate={
                          someCheckableStudentsSelected &&
                          !allCheckableStudentsSelected
                        }
                        disabled={
                          checkableStudents.length === 0 || checkInSubmitting
                        }
                        onChange={(event) =>
                          handleSelectAllStudents(event.target.checked)
                        }
                        inputProps={{ 'aria-label': 'Select all students' }}
                        sx={{
                          color: '#fff',
                          '&.Mui-checked': { color: '#fff' },
                          '&.MuiCheckbox-indeterminate': { color: '#fff' },
                        }}
                      />
                    </TableCell>
                    <ManagementTableHeaderCell>
                      Last Name
                    </ManagementTableHeaderCell>
                    <ManagementTableHeaderCell>
                      First Name
                    </ManagementTableHeaderCell>
                    <ManagementTableHeaderCell>
                      Preferred Name
                    </ManagementTableHeaderCell>
                    <ManagementTableHeaderCell>
                      Date of Birth
                    </ManagementTableHeaderCell>
                    <ManagementTableHeaderCell>Level</ManagementTableHeaderCell>
                    <ManagementTableHeaderCell>
                      User Type
                    </ManagementTableHeaderCell>
                    <ManagementTableHeaderCell>
                      Program
                    </ManagementTableHeaderCell>
                    <ManagementTableHeaderCell>Type</ManagementTableHeaderCell>
                    <ManagementTableHeaderCell>
                      Package
                    </ManagementTableHeaderCell>
                    <ManagementTableHeaderCell>
                      Sessions Remaining
                    </ManagementTableHeaderCell>
                    <ManagementTableHeaderCell>
                      Joined Date
                    </ManagementTableHeaderCell>
                    <ManagementTableHeaderCell>
                      Checked In Today
                    </ManagementTableHeaderCell>
                    <ManagementTableHeaderCell>
                      Actions
                    </ManagementTableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedStudents.map((student) => (
                    <TableRow
                      key={student.id}
                      aria-disabled={isPackageExpired(
                        student.registrationEndDate,
                      )}
                      sx={
                        isPackageExpired(student.registrationEndDate)
                          ? {
                              backgroundColor: alpha(
                                theme.palette.warning.main,
                                0.18,
                              ),
                              '&:hover': {
                                backgroundColor: alpha(
                                  theme.palette.warning.main,
                                  0.18,
                                ),
                              },
                            }
                          : undefined
                      }
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selectedStudentIds.has(
                            student.registrationId,
                          )}
                          disabled={student.checkedInToday || checkInSubmitting}
                          onChange={(event) =>
                            handleStudentSelectionChange(
                              student.registrationId,
                              event.target.checked,
                            )
                          }
                          inputProps={{
                            'aria-label': `Select ${student.name}`,
                          }}
                        />
                      </TableCell>
                      <TableCell>{student.lastName}</TableCell>
                      <TableCell>{student.firstName}</TableCell>
                      <TableCell>{student.preferredName || 'None'}</TableCell>
                      <TableCell>{formatStudentDate(student.dob)}</TableCell>
                      <TableCell sx={levelColumnSx}>
                        <SofLevelChip
                          level={student.level}
                          serviceType={student.type}
                        />
                      </TableCell>
                      <TableCell>
                        <ColorChip
                          label={student.userType}
                          palette={userTypeColors}
                        />
                      </TableCell>
                      <TableCell>
                        <ColorChip
                          label={student.program}
                          palette={serviceColors}
                        />
                      </TableCell>
                      <TableCell>
                        <ColorChip
                          label={student.type}
                          palette={serviceTypeColors}
                        />
                      </TableCell>
                      <TableCell>
                        <ButtonBase
                          onClick={(event) => handleOpenPackage(event, student)}
                          aria-label={`View package information for ${student.name}`}
                          sx={{
                            minWidth: 0,
                            p: 0,
                            borderRadius: 0,
                            justifyContent: 'flex-start',
                            color: 'inherit',
                            font: 'inherit',
                            '&:hover': {
                              cursor: 'pointer',
                              backgroundColor: 'transparent',
                            },
                            '&:hover .package-text': {
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          <Typography
                            component="span"
                            className="package-text"
                            variant="body2"
                            sx={{ color: 'inherit', textAlign: 'left' }}
                          >
                            {student.details}
                          </Typography>
                        </ButtonBase>
                      </TableCell>
                      <TableCell>
                        {student.visitType === 'count'
                          ? (student.remainingSessions ?? '---')
                          : '∞'}
                      </TableCell>
                      <TableCell>
                        {formatStudentDate(student.joinedDate)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <ColorChip
                          label={
                            student.checkedInToday
                              ? 'Checked In'
                              : 'Not Checked In'
                          }
                          palette={checkInStatusColors}
                          paletteKey={
                            student.checkedInToday
                              ? 'CHECKED_IN'
                              : 'NOT_CHECKED_IN'
                          }
                          icon={
                            student.checkedInToday ? (
                              <CheckCircle sx={{ fontSize: 16 }} />
                            ) : (
                              <Cancel sx={{ fontSize: 16 }} />
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="View check-in history">
                            <IconButton
                              size="small"
                              color="primary"
                              aria-label={`View check-in history for ${student.name}`}
                              onClick={(event) =>
                                handleOpenHistory(event, student)
                              }
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              student.checkedInToday
                                ? 'Already checked in today'
                                : 'Check in'
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                disabled={student.checkedInToday}
                                aria-label={`Check in ${student.name}`}
                                onClick={(event) =>
                                  handleOpenCheckIn(event, student)
                                }
                              >
                                <Done fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {selectedClass && filteredStudents.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={14} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {selectedClass.students.length === 0
                            ? 'No students found'
                            : 'No students match your search'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            component="div"
            count={filteredStudents.length}
            page={studentPage}
            onPageChange={(_, nextPage) => setStudentPage(nextPage)}
            rowsPerPage={studentRowsPerPage}
            onRowsPerPageChange={handleStudentRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Rows per page:"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedClassId(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(packageStudent)}
        onClose={() => setPackageStudent(null)}
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Package Information</DialogTitle>
        <DialogContent dividers>
          {packageStudent && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Student
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {packageStudent.name}
                </Typography>
              </Box>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small" aria-label="Package information">
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor: MANAGEMENT_TABLE_HEADER_COLOR,
                      }}
                    >
                      <ManagementTableHeaderCell>
                        Course
                      </ManagementTableHeaderCell>
                      <ManagementTableHeaderCell>
                        Registration Start Date
                      </ManagementTableHeaderCell>
                      <ManagementTableHeaderCell>
                        Expiry Date
                      </ManagementTableHeaderCell>
                      <ManagementTableHeaderCell>
                        Sessions Remaining
                      </ManagementTableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getPackageRows(packageStudent).map((coursePackage) => (
                      <TableRow key={coursePackage.id}>
                        <TableCell>{coursePackage.course}</TableCell>
                        <TableCell>
                          {formatPackageDate(
                            coursePackage.registrationStartDate,
                          )}
                        </TableCell>
                        <TableCell>
                          {formatPackageDate(coursePackage.registrationEndDate)}
                        </TableCell>
                        <TableCell>
                          {coursePackage.visitType === 'count'
                            ? (coursePackage.remainingSessions ?? '---')
                            : '∞'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPackageStudent(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(checkInStudent)}
        onClose={() => !checkInSubmitting && setCheckInStudent(null)}
      >
        <DialogTitle>Confirm Checkin</DialogTitle>
        <DialogContent>Are you sure to checkin for this user?</DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCheckInStudent(null)}
            disabled={checkInSubmitting}
            color="primary"
          >
            Close
          </Button>
          <Button
            onClick={() => void handleConfirmCheckIn()}
            disabled={checkInSubmitting}
            color="primary"
          >
            {checkInSubmitting ? 'Checking...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        open={Boolean(historyStudent)}
        onClose={() => setHistoryStudent(null)}
      >
        <DialogTitle>Check in Information</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              my: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="MM/DD/YYYY"
                label="Filter by Check-in Date"
                value={historyFilterDate}
                onChange={(newDate) => {
                  setHistoryPage(0);
                  setHistoryFilterDate(newDate);
                }}
                slotProps={{
                  field: {
                    clearable: true,
                    onClear: () => {
                      setHistoryPage(0);
                      setHistoryFilterDate(null);
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Box>

          <TableContainer>
            <Table size="small" aria-label="Check-in history">
              <TableHead>
                <TableRow
                  sx={{ backgroundColor: MANAGEMENT_TABLE_HEADER_COLOR }}
                >
                  <ManagementTableHeaderCell>Date</ManagementTableHeaderCell>
                  <ManagementTableHeaderCell>Time</ManagementTableHeaderCell>
                  <ManagementTableHeaderCell>Actions</ManagementTableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyLoading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : history.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        No check-in found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  history.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        {new Date(item.attendanceDate).toLocaleDateString(
                          'en-US',
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(item.attendanceDate).toLocaleTimeString(
                          'en-US',
                          {
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                          },
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Undo check-in">
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label="Undo check-in"
                            onClick={() => setUndoAttendance(item)}
                          >
                            <Replay fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={historyTotalItems}
            page={historyPage}
            onPageChange={(_, nextPage) => setHistoryPage(nextPage)}
            rowsPerPage={historyRowsPerPage}
            onRowsPerPageChange={(event) => {
              setHistoryRowsPerPage(Number(event.target.value));
              setHistoryPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25]}
            labelRowsPerPage="Rows per page:"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryStudent(null)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(undoAttendance)}
        onClose={() => !undoSubmitting && setUndoAttendance(null)}
      >
        <DialogTitle>Confirm Undo Checkin</DialogTitle>
        <DialogContent>Are you sure to undo this check-in?</DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUndoAttendance(null)}
            disabled={undoSubmitting}
          >
            Close
          </Button>
          <Button
            onClick={() => void handleConfirmUndo()}
            disabled={undoSubmitting}
          >
            {undoSubmitting ? 'Undoing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CoachClassPage;
